import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Space from "antd/es/space";
import { FormattedMessage, useIntl } from "react-intl";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import { EntityTypeSupportingReports } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { Button, SearchField } from "@mapmycustomers/ui";

import UserMultiSelectField from "@app/component/input/UserMultiSelectField";
import EntityTypeMenu from "@app/scene/reports/components/Export/EntityTypeMenu";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import FilterBar from "./FilterBar";
import FilterType from "./FilterType";
import styles from "./Header.module.scss";

export interface HeaderProps {
  entityType?: EntityTypeSupportingReports;
  filterType: FilterType;
  onChangeEntityType: (entityType: EntityTypeSupportingReports | undefined) => void;
  onChangeFilterType: (filterType: FilterType) => void;
  onCreate: () => void;
  onSearch: (value: string) => void;
  onSelectUsers: (selectedUserIds: Array<User["id"]>) => void;
  reportCount: number;
  search: string;
  selectedUserIds: Array<User["id"]>;
  users: User[];
}

interface Props extends HeaderProps {
  canCreate: boolean;
}

const Header: React.FC<Props> = ({
  canCreate,
  entityType,
  filterType,
  onChangeEntityType,
  onChangeFilterType,
  onCreate,
  onSearch,
  onSelectUsers,
  reportCount,
  search,
  selectedUserIds,
  users,
}) => {
  const intl = useIntl();
  const handleSelectedUsers = useCallback(
    (selectedUser: Array<User["id"]>) => {
      onSelectUsers(selectedUser);
    },
    [onSelectUsers]
  );

  return (
    <div>
      <section className={styles.container}>
        <section className={styles.title}>
          <FormattedMessage
            id="reports.exports.header.title"
            defaultMessage="Exports{count, select, 0 {} other { ({count})}}"
            description="Title of the Export page"
            values={{
              count: reportCount,
            }}
          />
        </section>
        <div>
          {canCreate && (
            <Button
              icon={<FontAwesomeIcon icon={faPlus} size="sm" />}
              onClick={onCreate}
              type="primary"
            >
              {intl.formatMessage({
                id: "reports.exports.header.create",
                defaultMessage: "Create Export",
                description: "Label of the Create Report button",
              })}
            </Button>
          )}
        </div>
      </section>
      <section className={styles.container}>
        <section className={styles.recordTypes}>
          <Space size="middle">
            <FilterBar
              className={styles.filterBar}
              filterType={filterType}
              onChangeFilterType={onChangeFilterType}
            />
            <EntityTypeMenu onChange={onChangeEntityType} value={entityType} />
            <UserMultiSelectField
              className={styles.userFilter}
              onChange={handleSelectedUsers}
              oneLiner
              placeholder={intl.formatMessage({
                id: "reports.exports.header.creatorFilter.placeholder",
                defaultMessage: "Click and type to select creators",
                description: "Placeholder of the Creator filter on exports",
              })}
              size="middle"
              users={users}
              value={selectedUserIds}
            />
          </Space>
        </section>
        <SearchField
          className={styles.searchBar}
          onChange={onSearch}
          placeholder={intl.formatMessage({
            id: "exports.header.searchbar.placeholder",
            defaultMessage: "Search",
            description: "Search bar placeholder for exports header",
          })}
          value={search}
        />
      </section>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canCreate: hasPermission(state)(PermissionName.EXPORT_CREATE),
});

export default connect(mapStateToProps)(Header);
